import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { clamp, extractPixelSize, last, isNumber, extension, command, keyBinding, NamedShortcut, ExtensionPriority, PlainExtension, ExtensionTag, joinStyles, isEmptyArray } from '@remirror/core';
import { ExtensionNodeFormattingMessages } from '@remirror/messages';

var increaseIndentOptions = {
  label: _ref => {
    var t = _ref.t;
    return t(ExtensionNodeFormattingMessages.INCREASE_INDENT_LABEL);
  },
  icon: 'indentIncrease'
};
var decreaseIndentOptions = {
  label: _ref2 => {
    var t = _ref2.t;
    return t(ExtensionNodeFormattingMessages.DECREASE_INDENT_LABEL);
  },
  icon: 'indentDecrease'
};
var centerAlignOptions = {
  label: _ref3 => {
    var t = _ref3.t;
    return t(ExtensionNodeFormattingMessages.CENTER_ALIGN_LABEL);
  },
  icon: 'alignCenter',
  active: createActiveCheck(_ref4 => {
    var node = _ref4.node;
    return node.attrs.textAlign === 'center';
  })
};
var justifyAlignOptions = {
  label: _ref5 => {
    var t = _ref5.t;
    return t(ExtensionNodeFormattingMessages.JUSTIFY_ALIGN_LABEL);
  },
  icon: 'alignJustify',
  active: createActiveCheck(_ref6 => {
    var node = _ref6.node;
    return node.attrs.textAlign === 'justify';
  })
};
var rightAlignOptions = {
  label: _ref7 => {
    var t = _ref7.t;
    return t(ExtensionNodeFormattingMessages.RIGHT_ALIGN_LABEL);
  },
  icon: 'alignRight',
  active: createActiveCheck(_ref8 => {
    var node = _ref8.node;
    return node.attrs.textAlign === 'right';
  })
};
var leftAlignOptions = {
  label: _ref9 => {
    var t = _ref9.t;
    return t(ExtensionNodeFormattingMessages.LEFT_ALIGN_LABEL);
  },
  icon: 'alignLeft',
  active: createActiveCheck(_ref10 => {
    var node = _ref10.node;
    return node.attrs.textAlign === 'left';
  })
};

function createActiveCheck(predicate) {
  return (_ref11, store) => {
    var excludeNodes = _ref11.excludeNodes;
    var getState = store.getState,
        nodeTags = store.nodeTags;
    var gatheredNodes = gatherNodes(getState(), nodeTags.formattingNode, excludeNodes);
    return gatheredNodes.some(predicate);
  };
}

function isValidNodeType(type, included, excluded) {
  if (excluded.includes(type.name)) {
    return false;
  }

  return included.includes(type.name);
}

function gatherNodes(state, included, excluded) {
  var gatheredNodes = [];
  var _state$selection = state.selection,
      $from = _state$selection.$from,
      $to = _state$selection.$to;
  var range = $from.blockRange($to);

  if (!range) {
    return [];
  }

  var parent = range.parent,
      start = range.start,
      end = range.end;
  var isRangeEntireParentContent = parent.nodeSize - 2 === end - start;

  if (isRangeEntireParentContent && isValidNodeType(parent.type, included, excluded)) {
    return [{
      node: parent,
      pos: start - 1
    }];
  } // Gather the nodes to indent.


  state.doc.nodesBetween(start, end, (node, pos) => {
    if (pos < start || pos > end) {
      return;
    }

    if (isValidNodeType(node.type, included, excluded)) {
      gatheredNodes.push({
        node,
        pos
      });
      return false;
    }

    return;
  });
  return gatheredNodes;
}
/**
 * The data attribute for an indented node attribute.
 */

var NODE_INDENT_ATTRIBUTE = 'data-node-indent';
/**
 * The data attribute text alignment within a block node which accepts
 * formatting.
 */

var NODE_TEXT_ALIGNMENT_ATTRIBUTE = 'data-node-text-align';
/**
 * The data attribute for the line height of a node.
 */

var NODE_LINE_HEIGHT_ATTRIBUTE = 'data-line-height-align';

/**
 * Extract the indent index from a list of indents and left margin.
 */
function extractIndent(indents, marginLeft) {
  var _indents$length;

  var largestIndent = extractPixelSize(last(indents));
  var value = extractPixelSize(marginLeft !== null && marginLeft !== void 0 ? marginLeft : '0');
  var max = (_indents$length = indents.length) !== null && _indents$length !== void 0 ? _indents$length : 1;
  var indentIncrement = largestIndent / max;
  return clamp({
    max,
    min: 0,
    value: Math.floor(value / indentIncrement)
  });
}
var NUMERIC_REGEX = /^\d+(?:\.\d+)?$/;
var PERCENT_REGEX = /^(\d+(?:\.\d+)?)%$/;
/**
 * Extract the line height from numeric or percentage line-height values
 */

function extractLineHeight(lineHeight) {
  if (isNumber(lineHeight)) {
    return lineHeight;
  }

  if (!lineHeight) {
    return null;
  }

  var lineHeightStr = lineHeight.trim();
  var percentMatch = lineHeightStr.match(PERCENT_REGEX);

  if (percentMatch) {
    return Number.parseFloat(percentMatch[1]) / 100;
  }

  var numberMatch = lineHeightStr.match(NUMERIC_REGEX);

  if (numberMatch) {
    return Number.parseFloat(numberMatch[0]);
  }

  return null;
}

var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _dec10, _dec11, _dec12, _dec13, _dec14, _dec15, _dec16, _class, _class2;
/**
 * Support consistent formatting of nodes within your editor.
 */

var NodeFormattingExtension = (_dec = extension({
  defaultOptions: {
    indents: ['0', '20px', '40px', '60px', '80px', '100px', '120px', '140px', '160px', '180px', '200px'],
    excludeNodes: []
  },
  staticKeys: ['indents']
}), _dec2 = command(), _dec3 = command(), _dec4 = command(), _dec5 = command(centerAlignOptions), _dec6 = command(justifyAlignOptions), _dec7 = command(leftAlignOptions), _dec8 = command(rightAlignOptions), _dec9 = command(increaseIndentOptions), _dec10 = command(decreaseIndentOptions), _dec11 = keyBinding({
  shortcut: NamedShortcut.CenterAlignment,
  command: 'centerAlign'
}), _dec12 = keyBinding({
  shortcut: NamedShortcut.JustifyAlignment,
  command: 'justifyAlign'
}), _dec13 = keyBinding({
  shortcut: NamedShortcut.LeftAlignment,
  command: 'leftAlign'
}), _dec14 = keyBinding({
  shortcut: NamedShortcut.RightAlignment,
  command: 'rightAlign'
}), _dec15 = keyBinding({
  shortcut: NamedShortcut.IncreaseIndent,
  command: 'increaseIndent',
  // Ensure this has lower priority than the indent keybinding in @remirror/extension-list
  priority: ExtensionPriority.Low
}), _dec16 = keyBinding({
  shortcut: NamedShortcut.DecreaseIndent,
  command: 'decreaseIndent',
  // Ensure this has higher priority than the dedent keybinding in @remirror/extension-list
  priority: ExtensionPriority.Medium
}), _dec(_class = (_class2 = class NodeFormattingExtension extends PlainExtension {
  get name() {
    return 'nodeFormatting';
  }
  /**
   * Set up the extra attributes which are applied to the formattable node
   * blocks.
   */


  createSchemaAttributes() {
    return [{
      identifiers: {
        type: 'node',
        tags: [ExtensionTag.FormattingNode],
        excludeNames: this.options.excludeNodes
      },
      attributes: {
        nodeIndent: this.nodeIndent(),
        nodeTextAlignment: this.nodeTextAlignment(),
        nodeLineHeight: this.nodeLineHeight(),
        style: {
          default: '',
          parseDOM: () => '',
          toDOM: _ref => {
            var nodeIndent = _ref.nodeIndent,
                nodeTextAlignment = _ref.nodeTextAlignment,
                nodeLineHeight = _ref.nodeLineHeight,
                style = _ref.style;
            var marginLeft = nodeIndent ? this.options.indents[nodeIndent] : undefined;
            var textAlign = nodeTextAlignment && nodeTextAlignment !== 'none' ? nodeTextAlignment : undefined;
            var lineHeight = nodeLineHeight ? nodeLineHeight : undefined;
            return {
              // Compose the style string together with the currently set style.
              style: joinStyles({
                marginLeft,
                textAlign,
                lineHeight
              }, style)
            };
          }
        }
      }
    }];
  }

  setLineHeight(lineHeight) {
    return this.setNodeAttribute(_ref2 => {
      var node = _ref2.node;

      if (lineHeight === node.attrs.nodeLineHeight) {
        return;
      }

      return {
        nodeLineHeight: lineHeight
      };
    });
  }
  /**
   * Set the text alignment for the selected nodes.
   */


  setTextAlignment(alignment) {
    return this.setNodeAttribute(_ref3 => {
      var node = _ref3.node;

      if (alignment === node.attrs.nodeTextAlignment) {
        return;
      }

      return {
        nodeTextAlignment: alignment
      };
    });
  }
  /**
   * Set the indent level for the selected nodes.
   */


  setIndent(level) {
    return this.setNodeAttribute(_ref4 => {
      var _node$attrs$nodeInden;

      var node = _ref4.node;
      var currentIndent = (_node$attrs$nodeInden = node.attrs.nodeIndent) !== null && _node$attrs$nodeInden !== void 0 ? _node$attrs$nodeInden : 0;
      var value = level === '-1' ? currentIndent - 1 : level === '+1' ? currentIndent + 1 : level;
      var indent = clamp({
        min: 0,
        max: this.options.indents.length - 1,
        value
      });

      if (indent === currentIndent) {
        return;
      }

      return {
        nodeIndent: indent
      };
    });
  }
  /**
   * Center the text within current block node.
   */


  centerAlign() {
    return this.setTextAlignment('center');
  }
  /**
   * Justify the text within the current block node.
   */


  justifyAlign() {
    return this.setTextAlignment('justify');
  }
  /**
   * Left align the text within the current block node.
   */


  leftAlign() {
    return this.setTextAlignment('left');
  }
  /**
   * Right align the text within the current block node.
   */


  rightAlign() {
    return this.setTextAlignment('right');
  }
  /**
   * Increase the indentation level of the current block node, if applicable.
   */


  increaseIndent() {
    return props => {
      return this.setIndent('+1')(props);
    };
  }
  /**
   * Decrease the indentation of the current block node.
   */


  decreaseIndent() {
    return props => {
      return this.setIndent('-1')(props);
    };
  }

  centerAlignShortcut(props) {
    return this.centerAlign()(props);
  }

  justifyAlignShortcut(props) {
    return this.justifyAlign()(props);
  }

  leftAlignShortcut(props) {
    return this.leftAlign()(props);
  }

  rightAlignShortcut(props) {
    return this.rightAlign()(props);
  }

  increaseIndentShortcut(props) {
    return this.increaseIndent()(props);
  }

  decreaseIndentShortcut(props) {
    return this.decreaseIndent()(props);
  }
  /**
   * Add an indentation attribute to the formattable node blocks.
   */


  nodeIndent() {
    return {
      default: null,
      parseDOM: element => {
        var _element$getAttribute;

        return (_element$getAttribute = element.getAttribute(NODE_INDENT_ATTRIBUTE)) !== null && _element$getAttribute !== void 0 ? _element$getAttribute : extractIndent(this.options.indents, element.style.marginLeft);
      },
      toDOM: attrs => {
        // Ignoring the `0` value is intentional here.
        if (!attrs.nodeIndent) {
          return;
        }

        var indentIndex = "".concat(attrs.nodeIndent);
        var marginLeft = this.options.indents[attrs.nodeIndent];

        if (!marginLeft) {
          return;
        }

        return {
          [NODE_INDENT_ATTRIBUTE]: indentIndex
        };
      }
    };
  }
  /**
   * Add the `nodeTextAlignment` attribute to the formattable block nodes.
   */


  nodeTextAlignment() {
    return {
      default: null,
      parseDOM: element => {
        var _element$getAttribute2;

        return (_element$getAttribute2 = element.getAttribute(NODE_TEXT_ALIGNMENT_ATTRIBUTE)) !== null && _element$getAttribute2 !== void 0 ? _element$getAttribute2 : element.style.textAlign;
      },
      toDOM: attrs => {
        var textAlign = attrs.nodeTextAlignment;

        if (!textAlign || textAlign === 'none') {
          return;
        }

        return {
          [NODE_TEXT_ALIGNMENT_ATTRIBUTE]: textAlign
        };
      }
    };
  }
  /**
   * Add a `line height` attribute to all the formattable block nodes selected.
   */


  nodeLineHeight() {
    return {
      default: null,
      parseDOM: element => {
        var _extractLineHeight;

        var val = element.getAttribute(NODE_LINE_HEIGHT_ATTRIBUTE);
        return (_extractLineHeight = extractLineHeight(val)) !== null && _extractLineHeight !== void 0 ? _extractLineHeight : extractLineHeight(element.style.lineHeight);
      },
      toDOM: attrs => {
        var lineHeight = attrs.nodeLineHeight;

        if (!lineHeight) {
          return;
        }

        return {
          [NODE_LINE_HEIGHT_ATTRIBUTE]: lineHeight.toString()
        };
      }
    };
  }

  setNodeAttribute(getAttributes) {
    return props => {
      var tr = props.tr,
          dispatch = props.dispatch;
      var gatheredNodes = gatherNodes(tr, this.store.nodeTags.formattingNode, this.options.excludeNodes);

      if (isEmptyArray(gatheredNodes)) {
        return false;
      }

      if (!dispatch) {
        return true;
      }

      var updates = [];

      var _iterator = _createForOfIteratorHelper(gatheredNodes),
          _step;

      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var _nodeWithPos = _step.value;
          var node = _nodeWithPos.node,
              _pos2 = _nodeWithPos.pos;

          var _attrs2 = getAttributes(_nodeWithPos);

          if (!_attrs2) {
            continue;
          }

          updates.push([_pos2, _objectSpread(_objectSpread({}, node.attrs), _attrs2)]);
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }

      if (isEmptyArray(updates)) {
        return false;
      }

      if (!dispatch) {
        return true;
      }

      for (var _i = 0, _updates = updates; _i < _updates.length; _i++) {
        var _updates$_i = _slicedToArray(_updates[_i], 2),
            _pos = _updates$_i[0],
            _attrs = _updates$_i[1];

        tr.setNodeMarkup(_pos, undefined, _attrs);
      }

      dispatch(tr);
      return true;
    };
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "setLineHeight", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "setLineHeight"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setTextAlignment", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "setTextAlignment"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setIndent", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "setIndent"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "centerAlign", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "centerAlign"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "justifyAlign", [_dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "justifyAlign"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "leftAlign", [_dec7], Object.getOwnPropertyDescriptor(_class2.prototype, "leftAlign"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "rightAlign", [_dec8], Object.getOwnPropertyDescriptor(_class2.prototype, "rightAlign"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "increaseIndent", [_dec9], Object.getOwnPropertyDescriptor(_class2.prototype, "increaseIndent"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "decreaseIndent", [_dec10], Object.getOwnPropertyDescriptor(_class2.prototype, "decreaseIndent"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "centerAlignShortcut", [_dec11], Object.getOwnPropertyDescriptor(_class2.prototype, "centerAlignShortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "justifyAlignShortcut", [_dec12], Object.getOwnPropertyDescriptor(_class2.prototype, "justifyAlignShortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "leftAlignShortcut", [_dec13], Object.getOwnPropertyDescriptor(_class2.prototype, "leftAlignShortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "rightAlignShortcut", [_dec14], Object.getOwnPropertyDescriptor(_class2.prototype, "rightAlignShortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "increaseIndentShortcut", [_dec15], Object.getOwnPropertyDescriptor(_class2.prototype, "increaseIndentShortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "decreaseIndentShortcut", [_dec16], Object.getOwnPropertyDescriptor(_class2.prototype, "decreaseIndentShortcut"), _class2.prototype)), _class2)) || _class);

export { NodeFormattingExtension };
